import { useEffect } from 'react';
import { ClientResponseError } from 'pocketbase';

import type { SimulatorState } from '@/domain/types';
import { pb } from '@/lib/pocketbase';
import { useSimulatorState, type SimulatorStore } from '@/store/useSimulatorState';

function extractSimulatorState(store: SimulatorStore): SimulatorState {
  return {
    version: store.version,
    currency: store.currency,
    info: store.info,
    initialDeficit: store.initialDeficit,
    team: store.team,
    categories: store.categories,
    fixedCosts: store.fixedCosts,
    recurringCosts: store.recurringCosts,
    tiers: store.tiers,
    acquisition: store.acquisition,
    projection: store.projection,
  };
}

export function useServerPersistence(): void {
  // Chaque champ est sélectionné individuellement : s'abonner au store entier
  // rendrait l'effet dépendant de `isSaving`/`lastError`, que la sauvegarde met
  // elle-même à jour — l'effet se relancerait donc en boucle et réécrirait le
  // document toutes les 500 ms, écrasant en continu les écritures concurrentes
  // (serveur MCP, autre onglet) avec l'état local de cet onglet.
  const simulationId = useSimulatorState((s) => s.simulationId);
  const isLoading = useSimulatorState((s) => s.isLoading);
  const version = useSimulatorState((s) => s.version);
  const currency = useSimulatorState((s) => s.currency);
  const info = useSimulatorState((s) => s.info);
  const initialDeficit = useSimulatorState((s) => s.initialDeficit);
  const team = useSimulatorState((s) => s.team);
  const categories = useSimulatorState((s) => s.categories);
  const fixedCosts = useSimulatorState((s) => s.fixedCosts);
  const recurringCosts = useSimulatorState((s) => s.recurringCosts);
  const tiers = useSimulatorState((s) => s.tiers);
  const acquisition = useSimulatorState((s) => s.acquisition);
  const projection = useSimulatorState((s) => s.projection);

  useEffect(() => {
    if (!simulationId || isLoading) return;
    const state: SimulatorState = {
      version,
      currency,
      info,
      initialDeficit,
      team,
      categories,
      fixedCosts,
      recurringCosts,
      tiers,
      acquisition,
      projection,
    };
    const title = state.info?.title?.trim() || 'Sans titre';

    const t = setTimeout(() => {
      useSimulatorState.setState({ isSaving: true });
      pb.collection('simulations')
        .update(simulationId, { state, title })
        .then(() => useSimulatorState.setState({ isSaving: false, lastError: null }))
        .catch((err: unknown) => {
          const message =
            err instanceof ClientResponseError ? err.message : 'Échec de la sauvegarde.';
          useSimulatorState.setState({ isSaving: false, lastError: message });
        });
    }, 500);

    return () => clearTimeout(t);
  }, [
    simulationId,
    isLoading,
    version,
    currency,
    info,
    initialDeficit,
    team,
    categories,
    fixedCosts,
    recurringCosts,
    tiers,
    acquisition,
    projection,
  ]);
}

export { extractSimulatorState };
